import { Flex, Icon, Box, Text } from '@cad-arc';
import { DELAY_FOR_UNIT_UPDATE } from '../../configuration';
import { LastUpdateTime } from './last-update-time';
import { CommonSize, getBasicCommonSize, getOneSizeSmaller } from '../../../common/helpers/utils';

type EntityAddressProps = {
    shouldTitleBeDisplayed?: boolean;
    isGpsLocation?: boolean;
    shouldSearchReverseGeocodeLocation?: boolean;
    addressMessage?: string;
    source?: string;
    placeName?: string;
    areGeoLocationCoordinatesVerified?: boolean;
    areGpsLocationCoordinatesVerified?: boolean;
    latitudeCoordinate?: number;
    longitudeCoordinate?: number;
    displayLineFirst?: string;
    displayLineLast?: string;
    displayLineOptionalMiddle?: string;
    subpremiseSupport?: boolean;
    displayLineSubPremises?: string;
    locationTimestamp?: number | Date;
    stationFullAddress?: string;
    size?: CommonSize;
};

const SourceDataBlock = ({ source = '', size }: { source?: string; size?: CommonSize }) => {
    const smallerSize = getOneSizeSmaller(size);

    return (
        <Box
            display="inline-block"
            data-testid="MAP_ENTITY_ADDRESS_SOURCE_DATA_BLOCK"
            marginTop="-2px"
        >
            <Icon
                icon="VerifiedLocation"
                color="positive.default"
                size={getBasicCommonSize(smallerSize)}
            />
            {!!source && (
                <Box display="inline-block" marginLeft={1}>
                    <Text fontWeight="semibold" color="positive" fontSize={smallerSize}>
                        {source}
                    </Text>
                </Box>
            )}
        </Box>
    );
};

const PlaceNameBlock = ({
    placeName,
    areCoordinatesVerified,
    source,
    size,
}: {
    placeName: string;
    areCoordinatesVerified?: boolean;
    source?: string;
    size?: CommonSize;
}) => (
    <Flex>
        <Text fontWeight="semibold" style={{ wordBreak: 'break-word' }} fontSize={size}>
            {placeName}
        </Text>
        {areCoordinatesVerified && <SourceDataBlock source={source} size={size} />}
    </Flex>
);

export function EntityAddress({
    isGpsLocation,
    source,
    placeName,
    areGeoLocationCoordinatesVerified,
    areGpsLocationCoordinatesVerified,
    displayLineFirst,
    displayLineLast,
    displayLineOptionalMiddle,
    subpremiseSupport,
    displayLineSubPremises,
    latitudeCoordinate,
    longitudeCoordinate,
    locationTimestamp,
    stationFullAddress,
    addressMessage,
    shouldTitleBeDisplayed = true,
    shouldSearchReverseGeocodeLocation = false,
    size,
}: EntityAddressProps): React.ReactElement {
    const smallerSize = getOneSizeSmaller(size);

    if (addressMessage) {
        return (
            <Box marginBottom={2} width="100%" minHeight={60} alignContent="center">
                <Text fontSize={smallerSize}>{addressMessage}</Text>
            </Box>
        );
    }
    if (isGpsLocation && !shouldSearchReverseGeocodeLocation) {
        if (latitudeCoordinate !== undefined && longitudeCoordinate !== undefined) {
            return (
                <Box marginBottom={2} width="100%">
                    <Text fontSize={size} style={{ wordBreak: 'break-word' }}>
                        {`${latitudeCoordinate}, ${longitudeCoordinate}`}
                    </Text>
                </Box>
            );
        }

        if (placeName) {
            return (
                <Box marginBottom={2} width="100%">
                    <PlaceNameBlock
                        placeName={placeName}
                        source={source}
                        areCoordinatesVerified={
                            isGpsLocation
                                ? areGpsLocationCoordinatesVerified
                                : areGeoLocationCoordinatesVerified
                        }
                        size={size}
                    />
                </Box>
            );
        }
        return (
            <Box marginBottom={2} width="100%">
                --
            </Box>
        );
    }

    const fullAddressPart =
        placeName || displayLineFirst || displayLineOptionalMiddle || displayLineLast;

    const isEmptyAddress = subpremiseSupport
        ? !fullAddressPart && !displayLineSubPremises
        : !fullAddressPart;

    return (
        <Box marginBottom={2} width="100%">
            {shouldTitleBeDisplayed && (
                <Box marginBottom={1.5}>
                    <Text fontSize={smallerSize}>Address</Text>
                </Box>
            )}
            {placeName && (
                <PlaceNameBlock
                    placeName={placeName}
                    source={source}
                    areCoordinatesVerified={areGeoLocationCoordinatesVerified}
                    size={size}
                />
            )}

            {displayLineFirst && (
                <Text style={{ wordBreak: 'break-word' }} fontSize={size}>
                    {displayLineFirst}
                </Text>
            )}
            {subpremiseSupport && displayLineSubPremises && (
                <Text style={{ wordBreak: 'break-word' }} fontSize={size}>
                    {displayLineSubPremises}
                </Text>
            )}
            {displayLineOptionalMiddle && (
                <Text style={{ wordBreak: 'break-word' }} fontSize={size}>
                    {displayLineOptionalMiddle}
                </Text>
            )}
            <Flex>
                <Text style={{ wordBreak: 'break-word' }} fontSize={size}>
                    {displayLineLast}{' '}
                </Text>
                {!placeName && !stationFullAddress && areGeoLocationCoordinatesVerified && (
                    <SourceDataBlock source={source} size={size} />
                )}
            </Flex>
            {stationFullAddress && (
                <Text fontWeight="semibold" style={{ wordBreak: 'break-word' }} fontSize={size}>
                    {stationFullAddress}{' '}
                    {areGeoLocationCoordinatesVerified && (
                        <Icon
                            icon="VerifiedLocation"
                            color="positive.default"
                            size={getBasicCommonSize(getOneSizeSmaller(size))}
                            data-testid="MAP_ENTITY_ADDRESS_STATION_VERIFIED_LOCATION_ICON"
                        />
                    )}
                </Text>
            )}
            {isEmptyAddress && !stationFullAddress && <Text fontSize={size}>--</Text>}
            {!isEmptyAddress && locationTimestamp && (
                <LastUpdateTime
                    timeUpdateKey={fullAddressPart}
                    lastTimestamp={locationTimestamp}
                    delay={DELAY_FOR_UNIT_UPDATE}
                    size={size}
                    testid="MAP_ENTITY_ADDRESS_TIME_AT_GEO_LOCATION"
                />
            )}
        </Box>
    );
}
